import React from "react";
import useChargingSessionById from "../hooks/useChargingSessionById";
import { jsPDF } from "jspdf";
import { formatINR } from "../utils/formatINR";

const PaymentChargingSessions = () => {
  const { session, isLoading, error, message } = useChargingSessionById();

  const downloadPDF = (s) => {
    const doc = new jsPDF();

    doc.setFontSize(16);
    doc.text("Charging Session Receipt", 20, 20);

    doc.setFontSize(12);
    doc.text(`Station: ${s.StationName}`, 20, 35);
    doc.text(
      `Address: ${s.AddressLine1}, ${s.City}, ${s.State}, ${s.Country}`,
      20,
      43
    );

    doc.text(`-----------------------------------`, 20, 53);

    doc.text(`Session ID: ${s.ID}`, 20, 63);
    doc.text(`User ID: ${s.UserID}`, 20, 71);
    doc.text(`Vehicle: ${s.Make} ${s.Model}`, 20, 79);
    doc.text(`Gun ID: ${s.GunID}`, 20, 87);

    doc.text(`Start: ${new Date(s.SessionStart).toLocaleString()}`, 20, 95);
    doc.text(`End: ${new Date(s.SessionEnd).toLocaleString()}`, 20, 103);
    doc.text(`Energy Delivered: ${s.TotalEnergyKWh} kWh`, 20, 111);

    doc.text(`-----------------------------------`, 20, 121);

    doc.text(`Total Cost: ${formatINR(s.TotalCost)}`, 20, 131);
    doc.text(`GST: ${formatINR(s.GSTAmount)}`, 20, 139);
    doc.text(`Final Cost: ${formatINR(s.FinalCost)}`, 20, 147);

    doc.save(`receipt_${s.ID}.pdf`);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-gray-900 border-r-transparent mb-3"></div>
          <p className="text-gray-700 text-sm">Loading session details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white border border-gray-200 rounded px-6 py-4 max-w-md">
          <p className="text-gray-900 text-sm">Error: {message || error}</p>
        </div>
      </div>
    );
  }

  if (!session || session.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 text-sm">No session data found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {session.map((s) => (
          <div key={s.ID} className="bg-white border border-gray-200 shadow-sm">
            {/* Header Section */}
            <div className="border-b border-gray-200 px-8 py-6">
              <h1 className="text-2xl font-semibold tracking-tight text-ink mb-1">
                {s.StationName}
              </h1>
              <p className="text-sm text-gray-500 font-light">
                {s.AddressLine1}, {s.City}, {s.State}, {s.Country}
              </p>
            </div>

            {/* Content Section */}
            <div className="px-8 py-8">
              {/* Session Information */}
              <div className="mb-8">
                <h2 className="text-xs uppercase tracking-wider text-gray-500 font-medium mb-4">
                  Session Information
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-6">
                  <div>
                    <dt className="text-xs text-gray-500 mb-1">Session ID</dt>
                    <dd className="text-sm text-gray-900">{s.ID}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-gray-500 mb-1">User ID</dt>
                    <dd className="text-sm text-gray-900">{s.UserID}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-gray-500 mb-1">Status</dt>
                    <dd className="text-sm text-gray-900 capitalize">
                      {s.Status}
                    </dd>
                  </div>
                </div>
              </div>

              {/* Vehicle & Equipment */}
              <div className="mb-8">
                <h2 className="text-xs uppercase tracking-wider text-gray-500 font-medium mb-4">
                  Vehicle & Equipment
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-6">
                  <div>
                    <dt className="text-xs text-gray-500 mb-1">Vehicle</dt>
                    <dd className="text-sm text-gray-900">
                      {s.Make} {s.Model}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs text-gray-500 mb-1">Vehicle ID</dt>
                    <dd className="text-sm text-gray-900">{s.VehicleID}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-gray-500 mb-1">Gun ID</dt>
                    <dd className="text-sm text-gray-900">{s.GunID}</dd>
                  </div>
                </div>
              </div>

              {/* Charging Details */}
              <div className="mb-8">
                <h2 className="text-xs uppercase tracking-wider text-gray-500 font-medium mb-4">
                  Charging Details
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-6">
                  <div>
                    <dt className="text-xs text-gray-500 mb-1">Start Time</dt>
                    <dd className="text-sm text-gray-900">
                      {new Date(s.SessionStart).toLocaleString()}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs text-gray-500 mb-1">End Time</dt>
                    <dd className="text-sm text-gray-900">
                      {new Date(s.SessionEnd).toLocaleString()}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs text-gray-500 mb-1">Duration</dt>
                    <dd className="text-sm text-gray-900">
                      {s.TotalDurationSecs} seconds
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs text-gray-500 mb-1">
                      Energy Delivered
                    </dt>
                    <dd className="text-sm text-gray-900">
                      {s.TotalEnergyKWh} kWh
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs text-gray-500 mb-1">Start SOC</dt>
                    <dd className="text-sm text-gray-900">{s.StartSOC}%</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-gray-500 mb-1">End SOC</dt>
                    <dd className="text-sm text-gray-900">{s.EndSOC}%</dd>
                  </div>
                </div>
              </div>

              {/* Billing */}
              <div className="border-t border-gray-200 pt-6">
                <h2 className="text-xs uppercase tracking-wider text-gray-500 font-medium mb-4">
                  Billing
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-6">
                  <div>
                    <dt className="text-xs text-gray-500 mb-1">Total Cost</dt>
                    <dd className="text-sm text-gray-900">{formatINR(s.TotalCost)}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-gray-500 mb-1">GST</dt>
                    <dd className="text-sm text-gray-900">{formatINR(s.GSTAmount)}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-gray-500 mb-1">Final Cost</dt>
                    <dd className="text-base font-medium text-gray-900">
                      {formatINR(s.FinalCost)}
                    </dd>
                  </div>
                </div>
              </div>

              {/* ✅ Download Button */}
              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => downloadPDF(s)}
                  className="px-4 py-2 text-sm bg-gray-900 text-white rounded hover:bg-gray-700"
                >
                  Download Receipt
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PaymentChargingSessions;
